import { create } from 'zustand';
import type { Answer, Answers, QuestionId, QuizResult } from './types';
import { QUESTIONS } from './schema';

interface QuizState {
  currentStep: number;
  answers: Answers;
  result: QuizResult | null;
  startedAt: string | null;

  setAnswer: (questionId: QuestionId, answer: Answer) => void;
  next: () => void;
  prev: () => void;
  goToStep: (step: number) => void;
  reset: () => void;
  setResult: (result: QuizResult | null) => void;
  restoreFromPersistence: (payload: {
    currentStep: number;
    answers: Answers;
    result: QuizResult | null;
    startedAt: string | null;
  }) => void;
}

const MAX_STEP = QUESTIONS.length - 1;

/**
 * Returns true when this question should be shown in the current flow.
 * No predicate ⇒ always visible.
 */
function isVisible(questionIdx: number, answers: Answers): boolean {
  const q = QUESTIONS[questionIdx];
  if (!q) return false;
  return q.visible_when ? q.visible_when(answers) : true;
}

/**
 * Walk forward from `from + 1` until we find the next visible question or
 * step past the end. Returns the new step index — clamped to MAX_STEP so
 * the existing "isLast → complete" logic in QuestionStep keeps working.
 */
function nextVisibleStep(from: number, answers: Answers): number {
  let i = from + 1;
  while (i <= MAX_STEP && !isVisible(i, answers)) i++;
  return Math.min(MAX_STEP, i);
}

/**
 * Walk backward from `from - 1` until we find a visible question or hit 0.
 */
function prevVisibleStep(from: number, answers: Answers): number {
  let i = from - 1;
  while (i > 0 && !isVisible(i, answers)) i--;
  return Math.max(0, i);
}

export const useQuizStore = create<QuizState>((set, get) => ({
  currentStep: 0,
  answers: {},
  result: null,
  startedAt: null,

  setAnswer: (questionId, answer) => {
    const prev = get();
    const isFirst = prev.startedAt == null && Object.keys(prev.answers).length === 0;
    const nextAnswers: Answers = { ...prev.answers, [questionId]: answer };
    set({
      answers: nextAnswers,
      startedAt: isFirst ? new Date().toISOString() : prev.startedAt,
    });
  },

  next: () => {
    set((s) => ({ currentStep: nextVisibleStep(s.currentStep, s.answers) }));
  },

  prev: () => {
    set((s) => ({ currentStep: prevVisibleStep(s.currentStep, s.answers) }));
  },

  goToStep: (step) => {
    const clamped = Math.max(0, Math.min(MAX_STEP, step));
    set({ currentStep: clamped });
  },

  reset: () => {
    set({ currentStep: 0, answers: {}, result: null, startedAt: null });
  },

  setResult: (result) => {
    set({ result });
  },

  restoreFromPersistence: (payload) => {
    const clamped = Math.max(0, Math.min(MAX_STEP, payload.currentStep));
    set({
      currentStep: clamped,
      answers: payload.answers,
      result: payload.result,
      startedAt: payload.startedAt,
    });
  },
}));

// Re-exported for use in QuizProgress and QuestionStep — they need to know
// the structural shape of the flow when computing position / "is last".
export { isVisible as isQuestionVisibleAt, nextVisibleStep, prevVisibleStep, MAX_STEP };
